import { ComponentPropsWithoutRef, ReactElement, cloneElement } from "react";
import { twMerge } from "tailwind-merge";
import Button, { BUTTON_STYLE, ButtonProps } from "./Button";

type OptionsTabsProps<T> = {
  onOptionChange: (value: T) => void;
  value?: T;
  options: OptionTab<T>[];
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
} & ComponentPropsWithoutRef<"div">;

export type OptionTab<T> = {
  label?: string;
  /** Leading adornment (icon). Sized to the tab's Button size. */
  icon?: ReactElement<HTMLDivElement>;
  /** Trailing adornment (badge, count, etc.) — left as-is, no size rewriting. */
  trailing?: ReactElement;
  value: T;
  tooltip?: string;
  disabled?: boolean;
};

const OptionsTabs = <T,>({
  onOptionChange,
  value,
  options,
  variant = "secondary",
  size = "m",
  ...rest
}: OptionsTabsProps<T>) => {
  const handleModeClick = (value: T) => {
    onOptionChange(value);
  };

  const containerStateClassName: Record<
    NonNullable<ButtonProps["variant"]>,
    string
  > = {
    primary: "",
    secondary: "",
    tertiary: "bg-transparent dark:bg-transparent",
    flat: "bg-transparent dark:bg-transparent",
    glass: "",
    ghost: "bg-transparent dark:bg-transparent",
    danger: "",
  };

  return (
    <div
      {...rest}
      id="options-tabs-wrapper"
      role="tablist"
      className={twMerge(
        `${rest.className} flex flex-wrap ${BUTTON_STYLE[size].radius} bg-surface-secondary`,
        containerStateClassName[variant],
        rest.className,
      )}
    >
      {options.map((option) => {
        const selected = option.value === value;
        const stateClassName = selected
          ? "[&.flat]:hover:!bg-surface-secondary"
          : // Inactive labels use foreground-secondary (not muted): on surface
            // cards (e.g. PropertyPannel in Create shorts) muted is too close to
            // the panel fill after the ui-theme token swap.
            // The enclosing <Dialog> forces `[&_button.tertiary:hover]:!bg-surface-secondary`
            // (higher specificity + !important), which would otherwise win over a
            // plain `hover:!bg-transparent`. Doubling `.tertiary` in the selector
            // (`[&.tertiary.tertiary]`) raises specificity above the Dialog rule so
            // unselected tabs stay transparent on hover. Same pattern as GenerateNewDialog.
            "!text-foreground-secondary dark:!text-foreground-muted hover:!text-foreground [&.tertiary.tertiary]:hover:!bg-transparent dark:[&.tertiary.tertiary]:hover:!bg-transparent";

        return (
          <Button
            className={twMerge(stateClassName, "shrink-0 flex-1")}
            data-tooltip-id="global"
            data-tooltip-content={option.tooltip}
            data-tooltip-place="top"
            data-tooltip-position-strategy="fixed"
            key={option.value as string}
            disabled={option.disabled}
            size={size}
            variant={selected ? variant : "tertiary"}
            onClick={() => handleModeClick(option.value)}
            role="tab"
            aria-selected={selected}
            aria-label={option.label ?? option.tooltip}
          >
            {option.icon
              ? cloneElement(option.icon, {
                  className: `${option.icon.props.className} ${
                    selected ? "" : ""
                  } ${BUTTON_STYLE.m.icon}`,
                })
              : null}
            {option.label}
            {/* Button treats unknown elements as icons (and clamps them to
                icon size). Wrap trailing in Content so badges keep their
                natural size — same pattern PricingTabs used before. */}
            {option.trailing ? (
              <Button.Content>{option.trailing}</Button.Content>
            ) : null}
          </Button>
        );
      })}
    </div>
  );
};

export default OptionsTabs;
